import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get, push, ref, set, update } from "firebase/database";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import {
  LuArrowLeft,
  LuCheck,
  LuChevronDown,
  LuImagePlus,
  LuLoaderCircle,
  LuPlus,
  LuSearch,
  LuX,
} from "react-icons/lu";
import { auth, database, storage } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import AppPage from "../components/AppPage";
import ModalSheet from "../components/ModalSheet";
import { BankPayoutForm } from "./BankPayouts";
import { NICHES } from "../utils/appData";
import { hasJobBank } from "../utils/jobDetails";
import {
  MAX_JOB_REFERENCES,
  MIN_POST_BUDGET,
  REFERENCE_PROVIDERS,
  jobReference,
  parsePostBudget,
  postDateError,
  postFields,
} from "../utils/postJob";

const input =
  "mt-2 min-h-[50px] w-full min-w-0 rounded-[13px] border border-slate-200 bg-white px-3.5 py-3 text-sm outline-none focus:border-[#327691] focus:ring-2 focus:ring-[#327691]/20 disabled:opacity-50 dark:border-[#363e46] dark:bg-[#23272c]";
const primary =
  "flex min-h-[50px] w-full items-center justify-center gap-2 rounded-full bg-[#327691] px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50";
const muted = "text-xs leading-5 text-slate-500 dark:text-slate-400";
const rand = (value) =>
  "R" +
  Number(value || 0).toLocaleString("en-ZA", { maximumFractionDigits: 2 });
function Field({ label, optional, hint, htmlFor, children }) {
  return (
    <div className="mb-5 min-w-0">
      <label
        htmlFor={htmlFor}
        className="flex items-baseline gap-2 text-sm font-semibold"
      >
        {label}
        {optional && (
          <span className="text-[11px] font-normal text-slate-400">
            Optional
          </span>
        )}
      </label>
      {hint && <p className={muted + " mt-1"}>{hint}</p>}
      {children}
    </div>
  );
}
export default function PostJob() {
  const location = useLocation();
  const { currentUser } = useUser();
  const params = new URLSearchParams(location.search);
  const jobId = params.get("jobId") || location.state?.jobId || "";
  return (
    <PostForm
      key={String(jobId) + ":" + (currentUser?.uid || "guest")}
      jobId={jobId}
      options={location.state || {}}
    />
  );
}
function PostForm({ jobId, options }) {
  const { currentUser, refreshUser } = useUser();
  const navigate = useNavigate();
  const uid = currentUser?.uid;
  const editing = Boolean(jobId);
  const [form, setForm] = useState({
    title: "",
    role: NICHES.includes(options.role) ? options.role : "",
    companyName: currentUser?.name || currentUser?.displayName || "",
    budget: "",
    rateUnit: "project",
    location: "",
    description: "",
    workStart: "",
    workEnd: "",
    deadline: "",
    datesFlexible: false,
  });
  const [references, setReferences] = useState([]);
  const [newLink, setNewLink] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const [adding, setAdding] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [preview, setPreview] = useState("");
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [bankOpen, setBankOpen] = useState(false);
  const [bankReady, setBankReady] = useState(hasJobBank(currentUser));
  const [focused, setFocused] = useState(false);
  const fileInput = useRef(null);
  const lock = useRef(false);
  const targetRef = useRef(null);
  useEffect(() => setBankReady(hasJobBank(currentUser)), [currentUser]);
  useEffect(() => {
    if (!image) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  useEffect(() => {
    if (!editing) return;
    let active = true;
    if (
      typeof jobId !== "string" ||
      [".", "#", "$", "[", "]", "/"].some((symbol) => jobId.includes(symbol))
    ) {
      setLoadError("Invalid job link.");
      setLoading(false);
      return;
    }
    get(ref(database, `Jobs/${jobId}`))
      .then((snapshot) => {
        if (!active) return;
        const job = snapshot.val();
        if (!job) throw new Error("This job no longer exists.");
        if (job.hostId !== uid)
          throw new Error("Only the job owner can edit this post.");
        setForm({
          title: job.title || "",
          role: job.role || "",
          companyName: job.companyName || "",
          budget: String(job.payRate || "").replace(/^[R$]\s*/i, ""),
          rateUnit: job.rateUnit || "project",
          location: job.location || "",
          description: job.description || "",
          workStart: job.workStart || "",
          workEnd: job.workEnd || "",
          deadline: job.deadline || job.neededBy || job.timeline || "",
          datesFlexible: Boolean(job.datesFlexible),
        });
        const raw = Array.isArray(job.references)
          ? job.references
          : Object.values(job.references || {});
        setReferences(
          raw.filter((item) => item?.url).slice(0, MAX_JOB_REFERENCES),
        );
        setImageUrl(job.imageUrl || "");
      })
      .catch((err) => {
        if (active) setLoadError(err.message || "Could not load this job.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [editing, jobId, uid]);
  const change = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));
  const budget = parsePostBudget(form.budget);
  const fee = Math.round(budget * 0.08 * 100) / 100;
  const dateError = postDateError(form);
  const valid = Boolean(
    form.title.trim() &&
    form.role &&
    budget >= MIN_POST_BUDGET &&
    form.location.trim() &&
    !dateError,
  );
  function changeDate(key, value) {
    setForm((previous) => {
      const next = { ...previous, [key]: value };
      if (key === "workStart" && next.workEnd && next.workEnd < value)
        next.workEnd = "";
      if (
        next.deadline &&
        (next.workEnd || next.workStart) &&
        next.deadline < (next.workEnd || next.workStart)
      )
        next.deadline = "";
      return next;
    });
  }
  function addReference() {
    setReferenceError("");
    const reference = jobReference(newLink);
    if (!reference) {
      setReferenceError("Use a supported public reference link.");
      return;
    }
    if (references.some((item) => item.url === reference.url)) {
      setNewLink("");
      setAdding(false);
      return;
    }
    if (references.length >= MAX_JOB_REFERENCES) return;
    setReferences((previous) => [...previous, reference]);
    setNewLink("");
    setAdding(false);
  }
  async function pickImage(event) {
    let file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setProcessing(true);
    try {
      const { compressImage, convertHeicToJpg } =
        await import("../utils/utils");
      if (
        /\.(heic|heif)$/i.test(file.name) ||
        /image\/hei[cf]/i.test(file.type)
      )
        file = await convertHeicToJpg(file);
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
        throw new Error("Choose a JPG, PNG, WebP or HEIC photo.");
      setImage(await compressImage(file));
    } catch (err) {
      setError(err.message || "Could not prepare the cover photo.");
    } finally {
      setProcessing(false);
    }
  }
  const checkUser = () => {
    if (!uid || auth.currentUser?.uid !== uid)
      throw new Error("Please sign in again to post a job.");
  };
  async function submit(event) {
    event.preventDefault();
    if (lock.current || busy || processing || loading || loadError) return;
    setError("");
    if (!valid) {
      setError(
        dateError ||
          "Enter a title, creative role, location and a budget of at least " +
            rand(MIN_POST_BUDGET) +
            ".",
      );
      return;
    }
    if (newLink.trim()) {
      setError("Add or discard the reference link before submitting.");
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      checkUser();
      const latestUser = (await get(ref(database, `Users/${uid}`))).val();
      if (!latestUser)
        throw new Error(
          "Your profile could not be loaded. Please sign in again.",
        );
      if (!hasJobBank(latestUser)) {
        setBankReady(false);
        setBankOpen(true);
        return;
      }
      if (editing) {
        const latestJob = (await get(ref(database, `Jobs/${jobId}`))).val();
        if (!latestJob || latestJob.hostId !== uid)
          throw new Error("Only the owner can edit this existing job.");
      }
      const target = editing
        ? ref(database, `Jobs/${jobId}`)
        : targetRef.current ||
          (targetRef.current = push(ref(database, "Jobs")));
      let cover = imageUrl;
      if (image) {
        const extension =
          image.type === "image/png"
            ? "png"
            : image.type === "image/webp"
              ? "webp"
              : "jpg";
        const destination = storageRef(
          storage,
          `jobs/images/${target.key}_${Date.now()}.${extension}`,
        );
        await uploadBytes(destination, image, { contentType: image.type });
        cover = await getDownloadURL(destination);
        setImageUrl(cover);
      }
      checkUser();
      const fields = postFields(
        form,
        { ...latestUser, uid },
        references,
        cover,
      );
      if (editing) await update(target, fields);
      else
        await set(target, {
          ...fields,
          id: target.key,
          status: "active",
          createdAt: Date.now(),
          env: "production",
        });
      window.showToast?.(
        editing ? "Changes submitted for review" : "Submitted for review",
        "success",
      );
      const returnTo =
        typeof options.returnTo === "string" &&
        /^\/user-profile\/[^/?#]+$/.test(options.returnTo)
          ? options.returnTo
          : "/jobs?mode=hire";
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.message || "Could not save your job. Please try again.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function checkBank() {
    setError("");
    try {
      checkUser();
      const latest = (await get(ref(database, `Users/${uid}`))).val();
      setBankReady(hasJobBank(latest));
      await refreshUser();
      if (!hasJobBank(latest)) {
        setError("Please finish saving your payout account first.");
        return;
      }
      setBankOpen(false);
    } catch (err) {
      setError(err.message);
    }
  }
  return (
    <AppPage
      title={editing ? "Edit job" : "Post a job"}
      hideMobileHeading
      headerActions={<span />}
      mainClassName="!pb-6"
    >
      <div className="w-full">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 hidden items-center gap-2 text-sm font-bold text-[#327691] dark:text-[#5FAFCB] md:inline-flex"
        >
          <LuArrowLeft size={18} />
          Back
        </button>
        {loading ? (
          <p role="status" className="py-12 text-center text-sm">
            Loading job…
          </p>
        ) : loadError ? (
          <p
            role="alert"
            className="rounded-xl bg-red-50 p-4 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {loadError}
          </p>
        ) : !uid ? (
          <p role="alert">Please sign in to post a job.</p>
        ) : (
          <form onSubmit={submit}>
            {options.hiringName && (
              <p className="mb-6 rounded-xl bg-[#327691]/10 p-4 text-sm leading-5 text-[#327691] dark:text-[#5FAFCB]">
                Post this job and {options.hiringName} can apply to it, along
                with anyone else who fits. Hires happen from the applicant list.
              </p>
            )}
            <fieldset
              disabled={busy || processing}
              className="grid min-w-0 gap-x-8 lg:grid-cols-2"
            >
              <div className="min-w-0">
                <Field label="Project title" htmlFor="post-title">
                  <input
                    id="post-title"
                    required
                    maxLength={180}
                    value={form.title}
                    onChange={(e) => change("title", e.target.value)}
                    placeholder="e.g. Brand video for a product launch"
                    className={input}
                  />
                </Field>
                <Field label="Creative role" htmlFor="post-role">
                  <button
                    id="post-role"
                    type="button"
                    onClick={() => {
                      setRoleSearch("");
                      setRoleOpen(true);
                    }}
                    className={
                      input +
                      " flex items-center justify-between gap-3 text-left"
                    }
                  >
                    <span>{form.role || "Select a role"}</span>
                    <LuChevronDown size={17} />
                  </button>
                </Field>
                <Field label="Company or brand" optional htmlFor="post-company">
                  <input
                    id="post-company"
                    value={form.companyName}
                    onChange={(e) => change("companyName", e.target.value)}
                    placeholder="Your name or brand"
                    className={input}
                  />
                </Field>
                <Field label="Budget" htmlFor="post-budget">
                  <div className={input + " flex items-center gap-2 !py-0"}>
                    <span className="font-semibold text-slate-500">R</span>
                    <input
                      id="post-budget"
                      inputMode="decimal"
                      required
                      value={form.budget}
                      onChange={(e) => change("budget", e.target.value)}
                      placeholder="0"
                      className="min-w-0 flex-1 bg-transparent py-3 font-semibold outline-none"
                    />
                  </div>
                  <div
                    className="mt-2.5 grid grid-cols-3 rounded-[13px] bg-slate-200/60 p-1 dark:bg-[#2c3036]"
                    role="group"
                    aria-label="Rate unit"
                  >
                    {[
                      ["project", "Per project"],
                      ["day", "Per day"],
                      ["hour", "Per hour"],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={form.rateUnit === key}
                        onClick={() => change("rateUnit", key)}
                        className={
                          "min-h-9 rounded-[10px] text-xs sm:text-sm " +
                          (form.rateUnit === key
                            ? "bg-white font-bold shadow-sm dark:bg-[#424950]"
                            : "text-slate-500 dark:text-slate-400")
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {form.budget.trim() && budget < MIN_POST_BUDGET && (
                    <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                      The minimum budget for a job is {rand(MIN_POST_BUDGET)}.
                    </p>
                  )}
                  {budget > 0 && (
                    <div className="mt-3 rounded-xl border border-black/5 bg-white p-3 text-xs dark:border-white/10 dark:bg-[#23272c]">
                      <div className="flex justify-between gap-3 py-1">
                        <span className="text-slate-500 dark:text-slate-400">
                          On the job board
                        </span>
                        <b>{rand(budget)}</b>
                      </div>
                      <div className="flex justify-between gap-3 py-1">
                        <span className="text-slate-500 dark:text-slate-400">
                          zylen fee · 8%, protects your payment
                        </span>
                        <span className="shrink-0">+ {rand(fee)}</span>
                      </div>
                      <div className="mt-2 flex justify-between gap-3 border-t border-black/5 pt-2 text-sm font-bold dark:border-white/10">
                        <span>You pay when you hire</span>
                        <span>{rand(budget + fee)}</span>
                      </div>
                    </div>
                  )}
                </Field>
                <Field label="Location" htmlFor="post-location">
                  <input
                    id="post-location"
                    required
                    maxLength={180}
                    value={form.location}
                    onChange={(e) => change("location", e.target.value)}
                    placeholder="e.g. Remote, Cape Town, Johannesburg"
                    className={input}
                  />
                </Field>
                <Field
                  label="Shoot date/s"
                  optional
                  hint="The days the creative needs to be on set or actively working. Leave the end date empty for a single day."
                >
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ["workStart", "Start"],
                      ["workEnd", "End"],
                    ].map(([key, label]) => (
                      <div key={key} className="min-w-0">
                        <label
                          htmlFor={"post-" + key}
                          className="mt-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400"
                        >
                          {label}
                        </label>
                        <input
                          id={"post-" + key}
                          aria-label={"Shoot " + label.toLowerCase() + " date"}
                          type="date"
                          disabled={form.datesFlexible || busy || processing}
                          value={form[key]}
                          min={key === "workEnd" ? form.workStart : undefined}
                          onChange={(e) => changeDate(key, e.target.value)}
                          className={input + " !mt-1"}
                        />
                      </div>
                    ))}
                  </div>
                </Field>
                <Field
                  label="Delivery deadline"
                  optional
                  htmlFor="post-deadline"
                  hint="When the finished work is due back."
                >
                  <input
                    id="post-deadline"
                    type="date"
                    disabled={form.datesFlexible || busy || processing}
                    min={form.workEnd || form.workStart || undefined}
                    value={form.deadline}
                    onChange={(e) => changeDate("deadline", e.target.value)}
                    className={input}
                  />
                </Field>
                <label className="mb-5 flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={form.datesFlexible}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((previous) => ({
                        ...previous,
                        datesFlexible: checked,
                        ...(checked
                          ? { workStart: "", workEnd: "", deadline: "" }
                          : {}),
                      }));
                    }}
                    className="mt-0.5 h-5 w-5 shrink-0 accent-[#327691]"
                  />
                  <span>
                    <b className="block text-sm font-semibold">
                      Dates flexible
                    </b>
                    <span className={muted}>
                      No fixed dates yet — you will agree them with whoever you
                      hire.
                    </span>
                  </span>
                </label>
                {dateError && (
                  <p role="alert" className="mb-4 text-xs text-red-600">
                    {dateError}
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <Field
                  label="What do you need?"
                  optional
                  htmlFor="post-description"
                >
                  <textarea
                    id="post-description"
                    rows={6}
                    maxLength={6000}
                    value={form.description}
                    onChange={(e) => change("description", e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder="Tell creatives about the shoot, deliverables, style and expectations."
                    className={input + " resize-y"}
                  />
                </Field>
                <Field
                  label="Reference or inspiration"
                  optional
                  hint="Link something that shows the look you are after. It is the fastest way to be understood."
                >
                  <div className="mt-3 space-y-3">
                    {references.map((item, index) => (
                      <div
                        key={item.url}
                        className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-[#363e46] dark:bg-[#23272c]"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <b className="text-xs text-[#327691] dark:text-[#5FAFCB]">
                              {item.platform || "Reference"}
                            </b>
                            <a
                              href={jobReference(item.url)?.url || undefined}
                              target="_blank"
                              rel="noreferrer"
                              className="block truncate text-xs text-slate-500"
                            >
                              {item.url}
                            </a>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setReferences((previous) =>
                                previous.filter((_, i) => i !== index),
                              )
                            }
                            aria-label={`Remove reference ${index + 1}`}
                            className="grid h-8 w-8 shrink-0 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            <LuX size={17} />
                          </button>
                        </div>
                        <textarea
                          aria-label={`What you like about reference ${index + 1}`}
                          rows={2}
                          value={item.note || ""}
                          onChange={(e) =>
                            setReferences((previous) =>
                              previous.map((r, i) =>
                                i === index
                                  ? { ...r, note: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          placeholder="What do you like about this? (optional)"
                          className={input + " text-xs"}
                        />
                      </div>
                    ))}
                  </div>
                  {adding && references.length < MAX_JOB_REFERENCES ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        aria-label="Reference link"
                        value={newLink}
                        onChange={(e) => setNewLink(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addReference();
                          }
                        }}
                        placeholder="Paste a link"
                        className={input + " !mt-0 flex-1"}
                      />
                      <button
                        type="button"
                        onClick={addReference}
                        className="min-h-11 rounded-xl bg-[#327691] px-4 text-sm font-bold text-white"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        aria-label="Discard reference"
                        onClick={() => {
                          setAdding(false);
                          setNewLink("");
                          setReferenceError("");
                        }}
                        className="p-2"
                      >
                        <LuX />
                      </button>
                    </div>
                  ) : references.length < MAX_JOB_REFERENCES ? (
                    <button
                      type="button"
                      onClick={() => setAdding(true)}
                      className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-[13px] border border-dashed border-[#327691]/50 text-sm font-semibold text-[#327691] dark:text-[#5FAFCB]"
                    >
                      <LuPlus size={16} />
                      Add a reference link
                    </button>
                  ) : null}
                  {referenceError && (
                    <p role="alert" className="mt-2 text-xs text-red-600">
                      {referenceError}
                    </p>
                  )}
                  <p className={muted + " mt-2"}>
                    {references.length} of {MAX_JOB_REFERENCES} ·{" "}
                    {REFERENCE_PROVIDERS.map(([name]) => name).join(", ")}.
                    Check the link is public before you post.
                  </p>
                </Field>
                <Field label="Cover photo" optional>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.heic,.heif"
                    onChange={pickImage}
                    className="hidden"
                  />
                  {preview || imageUrl ? (
                    <div className="relative mt-3 overflow-hidden rounded-[14px]">
                      <img
                        src={preview || imageUrl}
                        alt="Job cover"
                        className="max-h-72 w-full object-cover"
                      />
                      <button
                        type="button"
                        aria-label="Remove cover photo"
                        onClick={() => {
                          setImage(null);
                          setImageUrl("");
                        }}
                        className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white"
                      >
                        <LuX size={17} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInput.current?.click()}
                      className="mt-2 flex min-h-12 w-full items-center justify-center gap-2 rounded-[13px] border border-dashed border-[#327691]/50 text-sm font-semibold text-[#327691] dark:text-[#5FAFCB]"
                    >
                      <LuImagePlus size={17} />
                      Add a cover photo
                    </button>
                  )}
                </Field>
              </div>
            </fieldset>
            {error && (
              <p
                role="alert"
                className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error}
              </p>
            )}
            <footer
              className={
                "sticky bottom-0 z-10 mt-4 rounded-[22px] border border-black/5 bg-white p-4 pb-[max(16px,env(safe-area-inset-bottom))] shadow-lg dark:border-[#363e46] dark:bg-[#23272c] " +
                (focused ? "max-md:static" : "")
              }
            >
              <button
                type="submit"
                disabled={!valid || busy || processing}
                className={primary}
              >
                {(busy || processing) && (
                  <LuLoaderCircle size={18} className="animate-spin" />
                )}
                {processing
                  ? "Preparing photo…"
                  : busy
                    ? "Submitting…"
                    : editing
                      ? "Save changes"
                      : "Post job"}
              </button>
              <p className={muted + " mt-2 text-center"}>
                {editing
                  ? "Edited jobs go back to our team for review, then live again for creatives to apply to."
                  : "Your job goes to our team for review, then live for creatives to apply to."}
              </p>
            </footer>
          </form>
        )}
      </div>
      <ModalSheet
        open={roleOpen}
        onClose={() => setRoleOpen(false)}
        title="Creative role"
      >
        <label className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 p-3 dark:border-slate-600">
          <LuSearch size={17} />
          <input
            aria-label="Search creative roles"
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            placeholder="Search roles"
            className="min-w-0 flex-1 bg-transparent outline-none"
          />
        </label>
        <div className="space-y-1">
          {NICHES.filter((role) =>
            role.toLowerCase().includes(roleSearch.toLowerCase()),
          ).map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => {
                change("role", role);
                setRoleOpen(false);
              }}
              aria-pressed={form.role === role}
              className={`flex min-h-12 w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm ${form.role === role ? "bg-[#327691]/10 font-bold text-[#327691] dark:text-[#5FAFCB]" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              {role}
              {form.role === role && <LuCheck size={18} />}
            </button>
          ))}
        </div>
      </ModalSheet>
      <ModalSheet
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        title="Set up your bank account"
      >
        <p className={muted + " mb-4"}>
          Add your payout details before posting. Your job form stays here while
          you set up your account.
        </p>
        <BankPayoutForm externalLinksInNewTab />
        <button type="button" onClick={checkBank} className={primary + " mt-5"}>
          {bankReady ? "Return to job form" : "I've saved my bank details"}
        </button>
        <p className={muted + " mt-2 text-center"}>
          Once setup is complete, return to the form and tap Post job to submit.
        </p>
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
      </ModalSheet>
    </AppPage>
  );
}
